import React, { useState, useEffect, Fragment } from "react";
import { Modal, Form, FormGroup, Label, Input } from "reactstrap";
import moment from "moment";
import Select from "react-select";
import RsDateRangePicker from "../RsDateRangePicker";
import { URLS } from "../urls";
import { toast } from "react-toastify";
import { Close } from "@mui/icons-material";
import { dateWithNowTime, fetchActionsUtil, useCookie } from "../../utils";
import "../leaves/newleaves.css";

export default function ApplyPermissionModal({
  modalOpen,
  setModalOpen,
  getPermission,
  activeTab,
}) {
  const branch_id = useCookie("branch_id");

  const [state, setState] = useState({
    buttonText: "Submit",
    userId: "",
    applyDate: moment(new Date()).format("DD/MM/YYYY"),
    staff: "",
    users: [],
    comment: "",
    description: "",
    employee: {},
    employees: [],
    startDate: moment(new Date()).format("MM/DD/YYYY"),
    endDate: moment(new Date()).format("MM/DD/YYYY"),
    type: "",
    mode: "General",
    reoccurring: "",
    branches: [],
  });

  const [departments, setDepartments] = useState([]);
  const [isHoliday, setIsHoliday] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [holidayName, setHolidayName] = useState("");
  const [allBranches, setAllBranches] = useState([]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      fetchData();
    }
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setIsHoliday(activeTab === "holiday");
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const departmentData = fetchActionsUtil(
        `${URLS.mainSiteBackend}/users/departments`
      );

      const employeeData = fetchActionsUtil(
        `${URLS.backendEmployees}/get/${branch_id}`
      );

      const branchData = fetchActionsUtil(`${URLS.backendBranch}/get`);

      const [departmentResult, employeeResult, branchResult] =
        await Promise.all([departmentData, employeeData, branchData]);

      const branches = branchResult?.branches?.map((branch) => {
        return {
          value: branch._id,
          label: branch.name,
        };
      });

      const emp = employeeResult.employees?.map((em) => ({
        label: `${
          em.personalInfo.name.first ? em.personalInfo.name.first : ""
        } ${em.personalInfo.name.middle ? em.personalInfo.name.middle : ""} ${
          em.personalInfo.name.last ? em.personalInfo.name.last : ""
        }`,
        value: JSON.stringify({
          id: em._id,
          name: `${
            em.personalInfo.name.first ? em.personalInfo.name.first : ""
          } ${em.personalInfo.name.middle ? em.personalInfo.name.middle : ""} ${
            em.personalInfo.name.last ? em.personalInfo.name.last : ""
          }`,
        }),
      }));
      setState({
        ...state,
        employees: emp,
        branches: [
          {
            value: "all",
            label: "All Branches",
          },
          ...branches,
        ],
      });
      setDepartments(departmentResult?.data?.departments);
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  };

  const getUsersInDepartments = async (department) => {
    let response = await fetch(
      `${URLS.mainSiteBackend}/users/by-department/${department}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        // credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();
    setState({
      ...state,
      users: data?.users,
    });
  };

  const onChangeInputs = (e) => {
    const { name, value } = e.target;
    setState({
      ...state,
      [name]: value,
    });
  };

  const filterByDateRange = (event, picker) => {
    setState({
      ...state,
      startDate: picker.startDate.format("MM/DD/YYYY"),
      endDate: picker.endDate.format("MM/DD/YYYY"),
    });
    picker.element.value = `${picker.startDate.format(
      "MM/DD/YYYY"
    )} - ${picker.endDate.format("MM/DD/YYYY")}`;
  };

  const clearDateRange = (event, picker) => {
    setState({
      ...state,
      startDate: "",
      endDate: "",
    });

    picker.element.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setState({ ...state, buttonText: "Submitting ..." });
    setLoading(true);

    const {
      applyDate,
      description,
      comment,
      staff,
      employee,
      startDate,
      endDate,
      type,
      mode,
      reoccurring,
    } = state;

    let employeeName = "";
    let employeeId = null;
    if (mode === "Single") {
      if (Object.keys(employee).length < 1) {
        return toast.error("Please select an employee", {
          position: "top-right",
        });
      }

      employeeName = JSON.parse(employee).name;
      employeeId = JSON.parse(employee).id;
    } else {
      employeeName = "All Employee";
    }

    const isReoccurring = reoccurring === "Yes" ? true : false;

    let newWord = null;
    if (isHoliday) {
      newWord = holidayName.toLowerCase().includes("holiday")
        ? holidayName
        : `${holidayName} holiday`;
    }

    const requisition = {
      title: isHoliday
        ? `${newWord} Request`
        : `${employeeName}'s Permission Request`,
      description,
      type: isHoliday ? "Holiday" : "Permission",
      requestto: staff,
      amount: 0,
      status: "Pending",
      regdate: moment(new Date()).format("MM/DD/YYYY"),
      requestby: staff,
      vendor: "",
      comment,
      filledFormData: "",
      loanDetails: JSON.stringify({
        employeeName,
        employeeId,
        startDate,
        endDate,
        type,
        isReoccurring,
        holidayName: newWord,
      }),
    };

    const sendBody = {
      applyDate,
      status: "Pending",
      userID: employeeId,
      staff,
      description,
      comment,
      branch_id,
      endDate: dateWithNowTime(endDate, "MM/DD/YYYY"),
      startDate: dateWithNowTime(startDate, "MM/DD/YYYY"),
      type,
      mode,
      holidayName: newWord,
      reoccurring: isReoccurring,
      allBranches,
    };

    const formData = new FormData();
    Object.entries(requisition).forEach(([key, value]) => {
      formData.append(key, value);
    });

    const token = "dummy token";

    try {
      if (process.env.REACT_APP_HAS_INVEX === "true") {
        const res = await fetch(`${URLS.mainSiteBackend}/requisition/create`, {
          method: "POST",
          headers: {
            Authorization: `Token ${token}`,
          },
          body: formData,
        });

        if (res.ok) {
          const result = await res.json();
          const sendResult = await fetchActionsUtil(
            `${URLS.backendpermmission}`,
            "POST",
            token,
            {
              ...sendBody,
              requestid: result?.data?.requisition?.requestid,
            }
          );

          if (sendResult.success) {
            toast.success(
              `${isHoliday ? "Holiday" : "Permission"} Successfully Applied.`,
              {
                position: "top-right",
              }
            );
            getPermission();
            setState({
              ...state,
              buttonText: "Submit",
              staff: "",
              users: [],
              comment: "",
              description: "",
            });
            setLoading(false);
            setModalOpen(false);
          }
        }
      } else {
        toast.error("Please Connect to Invex ERP.", {
          position: "top-right",
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("An unexpected error occurred.", {
        position: "top-right",
      });
    } finally {
      setState({ ...state, buttonText: "Submit" });
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={modalOpen}
      toggle={() => setModalOpen(!modalOpen)}
      className="modal-alt"
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="">
          <h3 className="page-title text-16 page-title-alt p-0 m-0 fw-bold">
            {isHoliday ? "Create a holiday" : "Apply For Permission"}
          </h3>
          <p className=" light-text text-16 mt-1 font-400">
            Create a new {isHoliday ? "holiday" : "permission"} request
          </p>
        </div>
        <div className="btn-close-alt" onClick={() => setModalOpen(!modalOpen)}>
          <Close />
        </div>
      </div>

      <Form onSubmit={submit}>
        <section className="modal-body-alt">
          <div className="">
            <div className="">
              <div className="w-100">
                {!isHoliday ? (
                  <FormGroup className="mb-3 w-100">
                    <Label className="text-16 font-600  light-text mb-2">
                      Permission Mode <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      value={state?.mode}
                      name="mode"
                      onChange={onChangeInputs}
                      required
                    >
                      <option value=""></option>
                      <option value="General">General</option>
                      <option value="Single">Single</option>
                    </Input>
                  </FormGroup>
                ) : (
                  <div className="w-100">
                    <div className="form-group mb-3 form-focus">
                      <Label className="text-16 font-600  light-text mb-2">
                        Holiday name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter holiday name"
                        className="form-control-w-0"
                        value={holidayName}
                        name="holidayName"
                        onChange={(e) => setHolidayName(e.target.value)}
                        required={!isHoliday}
                      />
                    </div>
                    <div className="row">
                      <div className="col-6 mt-3">
                        <FormGroup className="w-100">
                          <Label className="text-16 font-600  light-text mb-2">
                            Choose Date <span className="text-danger">*</span>
                          </Label>

                          <RsDateRangePicker
                            placement="topEnd"
                            className="form-control-w-0 w-100"
                            onClean={(event, picker) =>
                              clearDateRange(event, picker)
                            }
                            onOk={(event, picker) =>
                              filterByDateRange(event, picker)
                            }
                          />
                        </FormGroup>
                      </div>
                      <div className="col-6 mt-3">
                        <FormGroup className="mb-3">
                          <Label className="text-16 font-600  light-text mb-2">
                            Reoccurrence <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            value={state?.reoccurring}
                            name="reoccurring"
                            onChange={onChangeInputs}
                            required={isHoliday}
                          >
                            <option value=""></option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </Input>
                        </FormGroup>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {!isHoliday ? (
                <div className="w-100 mb-3 mt-4 gap-4 d-flex">
                  {state?.mode === "Single" && (
                    <FormGroup
                      className={`${state?.mode === "Single" ? "w-50" : ""}`}
                    >
                      <Label className="text-16 font-600  light-text mb-2">
                        Employee <span className="text-danger">*</span>
                      </Label>

                      <Select
                        closeMenuOnSelect={true}
                        isSearchable
                        required
                        classNamePrefix="form-control-w-0"
                        value={state?.employees?.find(
                          (el) => el.value === state?.employee?.name
                        )}
                        onChange={(selected) => {
                          setState({ ...state, employee: selected.value });
                        }}
                        options={state?.employees}
                      />
                    </FormGroup>
                  )}

                  <FormGroup
                    className={`${state?.mode === "Single" ? "w-50" : "w-100"}`}
                  >
                    <Label className="text-16 font-600  light-text mb-2">
                      Choose Date <span className="text-danger">*</span>
                    </Label>

                    <RsDateRangePicker
                      placement="topEnd"
                      className="form-control-w-0 w-100"
                      onClean={(event, picker) => clearDateRange(event, picker)}
                      onOk={(event, picker) => filterByDateRange(event, picker)}
                    />
                  </FormGroup>
                </div>
              ) : null}
            </div>
            <FormGroup className="mb-3">
              <Label className="text-16 font-600  light-text mb-2">
                Description <span className="text-danger">*</span>
              </Label>
              <Label className=" p-0 rounded pb-2 message-area d-block">
                <Input
                  type="textarea"
                  name="description"
                  value={state?.description}
                  onChange={onChangeInputs}
                  className="w-100 form-control-text-area border-0 p-3"
                  placeholder="Enter your detail description"
                  rows={4}
                  required
                />
              </Label>
            </FormGroup>

            {!isHoliday ? (
              <FormGroup className="mb-3">
                <Label className="text-16 font-600  light-text mb-2">
                  Permission Type <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={state?.type}
                  name="type"
                  onChange={onChangeInputs}
                  required={!isHoliday}
                >
                  <option selected value="Select permission type">
                    Select permission type
                  </option>
                  <option value="NoSignOut">No SignOut</option>
                  <option value="Lateness">Lateness</option>
                  <option value="Absent">Absent</option>
                  <option value="EarlyClosure">Early Closure</option>
                </Input>
              </FormGroup>
            ) : (
              <div className="form-group">
                <label className="text-16 font-600  light-text mb-2">
                  Select Company
                </label>

                <Select
                  closeMenuOnSelect={true}
                  isMulti
                  isSearchable={true}
                  // value={
                  //   state?.branches?.length
                  //     ? state?.branches?.filter((d) =>
                  //         state?.branches?.find((e) => e.value === d.value)
                  //       )
                  //     : []
                  // }
                  onChange={(selected) => {
                    setAllBranches(selected);
                  }}
                  options={state.branches}
                />
              </div>
            )}

            <div className="d-flex align-items-center my-4">
              <h4 className="pr-2">Send Request To</h4>{" "}
              <hr className="flex-grow-1 m-0" />
            </div>

            <div className="row gap-3">
              <FormGroup className="col mb-4">
                <Label className="text-16 font-600  light-text mb-2">
                  Department <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  onChange={(e) => getUsersInDepartments(e.target.value)}
                  required
                >
                  <option value="">Select Recipient's Department</option>
                  {departments &&
                    departments.map((el, index) => (
                      <option key={index} value={el.Department}>
                        {el.Department}
                      </option>
                    ))}
                </Input>
              </FormGroup>

              {state?.users.length > 0 && (
                <FormGroup className="col mb-4">
                  <Label className="text-16 font-600  light-text mb-2">
                    Recipient <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    name="staff"
                    value={state?.staff}
                    onChange={onChangeInputs}
                    required
                  >
                    <option value="">Select Recipient </option>
                    {state?.users.map((el, index) => (
                      <Fragment key={index}>
                        <option value={el.Staff_ID}>{el.Name}</option>
                      </Fragment>
                    ))}
                  </Input>
                </FormGroup>
              )}
            </div>

            <FormGroup className="mb-3">
              <Label className="text-16 font-600  light-text mb-2">
                Comment :
              </Label>
              <Label className=" p-0 rounded pb-2 message-area d-block">
                <Input
                  type="textarea"
                  name="comment"
                  value={state?.comment}
                  onChange={onChangeInputs}
                  className="w-100 form-control-text-area border-0 p-3"
                  placeholder="Enter a comment"
                  rows={4}
                />
              </Label>
            </FormGroup>
          </div>
          <div className="submit-section d-flex align-items-center justify-content-between">
            <div className=""></div>
            <div className="d-flex align-items-center gap-4">
              <button
                type="button"
                className=" cancel-btn-alt"
                onClick={() => setModalOpen(!modalOpen)}
              >
                <p className="light-text font-600 text-14">Cancel</p>
              </button>
              <button
                type="submit"
                className=" submit-btn-alt"
                disabled={isLoading}
              >
                {state?.buttonText}
              </button>
            </div>
          </div>
        </section>
      </Form>
    </Modal>
  );
}
